/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useEffect,
  useMemo,
  useRef
} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {ClickAwayListener} from '@ion/dls/src/components/listeners/ClickAwayListener';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import IonPortal from '@ion/dls/src/render/IonPortal';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {resolveBasemapLayers, translateBasemap} from '../helpers/simpleMapUtils';
import {useBasemapGallery} from '../hooks/useBasemapGallery';
import {useShowTool} from '../hooks/useShowTool';
import {basemapTitle, MapTools} from '../types/types';
import {MapViewTool} from './MapViewTool';

// Defines the props for the BasemapGalleryTool component.
export type BasemapGalleryToolProps = {
  map: __esri.Map;
  view: __esri.MapView;
  createOSMBasemap: Function;
  onBasemapChanged: Function;
  isOpen?: boolean;
  activeTool: MapTools;
  setActiveTool: Function;
  isOpenFromBottomSheet?: boolean;
  setSelectedTool?: Function;
  usePortal?: boolean;
  isAdminGeofence?: boolean;
};

export const useStyles = makeStyles(theme => ({
  mapView : {
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    boxShadow       : dlsAlt('components', 'boxShadow', 'shadow4'),
    position        : 'absolute',
    top             : 100,
    right           : '55px',
    width           : 360
  },
  mapViewMobile : {
    border    : `1px solid ${dlsColor('grey5')}`,
    boxShadow : dlsAlt('components', 'boxShadow', 'shadow4'),
    position  : 'absolute',
    top       : '55px',
    right     : '0px',
    minWidth  : '200px',
    margin    : '4px'
  },
  galleryBottomSheetView : {
    backgroundColor : dlsColor('white'),
    top             : '55px',
    right           : '0px',
    minWidth        : '200px',
    padding         : '8px'
  },
  galleryHeaderContainer : {
    width        : '100%',
    height       : 26,
    background   : dlsColor('grey10'),
    borderRadius : '4px 4px 0px 0px'
  },
  galleryHeader : {
    display       : 'flex',
    marginLeft    : 12,
    height        : '100%',
    alignItems    : 'center',
    fontFamily    : 'Roboto !important',
    fontStyle     : 'normal',
    fontSize      : '12px !important',
    lineHeight    : '16px !important',
    letterSpacing : '0.5px !important',
    color         : dlsColor('white')
  }
}));

const BASEMAP_GALLERY_CONTAINER_ID = 'basemap-gallery-container';

/**
 * The BasemapGalleryTool component provides the UI which displays the associated Icon
 * button within a DLS Map that, when clicked, initiates the corresponding
 * feature.
 *
 * This MapViewTool represents the "Maps Tool" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1526%3A7422
 *
 * ```typescript
 *
 *   <BasemapGalleryTool map={map} view={view} onBasemapChanged={function() {}} />
 *
 * ```
 */
export const BasemapGalleryTool = ({
  map,
  view,
  createOSMBasemap,
  onBasemapChanged,
  activeTool,
  setActiveTool,
  setSelectedTool,
  isOpen = false,
  usePortal = false,
  isOpenFromBottomSheet = false
}: BasemapGalleryToolProps) => {
  const classes             = useStyles();
  const isPhone             = useIsPhone();
  const basemapGalleryRef   = useBasemapGallery({view, createOSMBasemap});
  const initialSelectionRef = useRef(null);
  const basemapWatchRef     = useRef(null);
  const childRef            = useRef(null);
  const [show, toggleShow, handleClickOutside, setShow] = useShowTool({
    ref          : childRef,
    initialState : isOpen,
    toolId       : MapTools.basemapGallery,
    activeTool,
    setActiveTool
  });

  useEffect(() => {
    setShow(isOpenFromBottomSheet);
  }, [isOpenFromBottomSheet]);

  function onBasemapUpdate(newValue, oldValue) {
    if (newValue !== oldValue) {
      resolveBasemapLayers(map);
      onBasemapChanged(newValue);
      setSelectedTool?.(null);
      // Give the gallery time to display before managing the initial
      // gallery selection.
      setTimeout(manageInitialSelection, 150);
    }
  };

  /**
   * The initial selection in the gallery is not always indicated by the ESRI
   * basemap gallery widget. This function ensures that the initial selection
   * will be indicated in the widget.
   *
   * The process for determining the initial basemap selection is as follows:
   *
   * - Are any basemap gallery entries selected in the widget?
   *   - If not:
   *     - Get the current map basemap.
   *     - Is the current map basemap's entry in the gallery's DOM yet?
   *       - If so, make sure the basemap's entry displays as selected in the
   *         gallery, and save a reference to this entry (used below).
   *       - If not, wait and try again.
   *   - If so, and if more than one gallery entry is selected, it means that
   *     an entry other than the initial entry has been selected by the user.
   *     Clear the selection of the initial entry; from now on the gallery
   *     widget ensures that only one entry will ever be selected.
   *
   */
  function manageInitialSelection() {
    const selectedClass = 'esri-basemap-gallery__item--selected';
    const elSelected = document.querySelectorAll(`.${selectedClass}`);
    if (!elSelected.length && basemapGalleryRef?.current) {
      const currentBasemap = basemapGalleryRef.current.activeBasemap.id;
      const elCurrentBasemap = document.querySelector(
        `[title="${basemapTitle[currentBasemap]}"]`
      );
      if (elCurrentBasemap) {
        elCurrentBasemap.classList?.add(selectedClass);
        initialSelectionRef.current = elCurrentBasemap;
      } else {
        setTimeout(manageInitialSelection, 150);
      }
    } else if (elSelected.length > 1) {
      initialSelectionRef.current?.classList?.remove(selectedClass);
    }
  };

  useEffect(() => {
    basemapWatchRef.current = map.watch('basemap.id', onBasemapUpdate);
    return function() {
      basemapWatchRef.current?.remove('basemap.id', onBasemapUpdate);
    };
  }, [basemapWatchRef]);

  useEffect(() => {
    if (basemapGalleryRef?.current) {
      basemapGalleryRef.current.container = BASEMAP_GALLERY_CONTAINER_ID;
      basemapGalleryRef.current.visible = show;
      if (show) {
        // Give the gallery time to display before managing the initial
        // gallery selection.
        setTimeout(manageInitialSelection, 150);
        translateBasemap();
      }
    }
  }, [show, basemapGalleryRef]);

  const BaseMapComponent = useMemo(() => (
    <div ref={childRef} style={{display: show ? 'inherit' : 'none'}}
      className={
        isOpenFromBottomSheet ? classes.galleryBottomSheetView : isPhone ? classes.mapViewMobile : classes.mapView
      } >
      <div className={classes.galleryHeaderContainer}>
        <HeaderTypography variant='h4' className={classes.galleryHeader}>
          {translate('maps:basemaps.header')}
        </HeaderTypography>
      </div>
      <div id={BASEMAP_GALLERY_CONTAINER_ID} />
    </div>
  ), [show, isPhone, isOpenFromBottomSheet, basemapGalleryRef]);

  if (isOpenFromBottomSheet) {
    return BaseMapComponent;
  }

  const component: any = (
    usePortal ? (
      <IonPortal id='basemap-gallery-component' show>
        {BaseMapComponent}
      </IonPortal>
    ) : (
      BaseMapComponent
    )
  );

  return (
    <>
      <MapViewTool isSelected={show}
        onClick={toggleShow} />
      <ClickAwayListener onClickAway={handleClickOutside} mountListener>
        {component}
      </ClickAwayListener>
    </>
  );
};

export default memo(BasemapGalleryTool);
